import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { cx } from '@emotion/css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useId from '@mui/material/utils/useId';
import styles from './Accordion.styles';
import { TitleSmallEmphasized } from '../Typography';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

interface AccordionContextValue {
  expandedItems: Set<string>;
  toggle: (id: string) => void;
  allowMultiple: boolean;
  headingLevel: HeadingLevel;
  panelIsLandmark: boolean;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

function useAccordionContext() {
  const ctx = useContext(AccordionContext);
  if (!ctx) {
    throw new Error('AccordionItem must be rendered inside an Accordion');
  }
  return ctx;
}

interface AccordionProps {
  children: React.ReactNode;
  /**
   * When true, multiple items can be open simultaneously.
   * Defaults to false (only one item open at a time).
   */
  allowMultiple?: boolean;
  /** IDs of items that should be expanded on first render. */
  defaultExpanded?: string[];
  /** Heading level for each item trigger wrapper. Defaults to 2. */
  headingLevel?: HeadingLevel;
  /**
   * When true, expanded panels expose `role="region"` for landmark navigation.
   * Defaults to false to avoid landmark proliferation with many panels.
   */
  panelIsLandmark?: boolean;
  className?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  children,
  allowMultiple = false,
  defaultExpanded = [],
  headingLevel = 2,
  panelIsLandmark = false,
  className,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set(defaultExpanded));

  const toggle = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!allowMultiple) {
          next.clear();
        }
        next.add(id);
      }
      return next;
    });
  };

  return (
    <AccordionContext.Provider
      value={{ expandedItems, toggle, allowMultiple, headingLevel, panelIsLandmark }}
    >
      <div className={cx(styles.root, className)}>{children}</div>
    </AccordionContext.Provider>
  );
};

interface AccordionItemProps {
  /** Unique identifier for this item within the Accordion. */
  id: string;
  title: string;
  children: React.ReactNode;
  /** Overrides the Accordion-level heading level for this item. */
  headingLevel?: HeadingLevel;
  /** Overrides the Accordion-level panel landmark setting for this item. */
  panelIsLandmark?: boolean;
  className?: string;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  title,
  children,
  headingLevel: itemHeadingLevel,
  panelIsLandmark: itemPanelIsLandmark,
  className,
}) => {
  const {
    expandedItems,
    toggle,
    headingLevel: accordionHeadingLevel,
    panelIsLandmark: accordionPanelIsLandmark,
  } = useAccordionContext();
  const headingLevel = itemHeadingLevel ?? accordionHeadingLevel;
  const panelIsLandmark = itemPanelIsLandmark ?? accordionPanelIsLandmark;
  const uid = useId();
  const triggerId = `accordion-trigger-${uid}-${id}`;
  const panelId = `accordion-panel-${uid}-${id}`;
  const isExpanded = expandedItems.has(id);
  const Heading = `h${headingLevel}` as keyof JSX.IntrinsicElements;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const panel = panelRef.current;
    if (!panel) return undefined;

    // ResizeObserver stays alive for the lifetime of the expanded panel so it catches
    // content that loads after the initial render (e.g. a loading → data transition).
    // The guard prevents scrolling when the panel is already fully in view, and we
    // scroll the accordion item div (trigger + panel) so the heading stays visible
    // and the user sees the beginning of the content, not the bottom of a tall list.
    // The effect cleanup owns the disconnect — no early disconnect inside the callback.
    const observer = new ResizeObserver(entries => {
      if (entries[0].contentRect.height > 0) {
        const rect = panel.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });

    observer.observe(panel);

    return () => observer.disconnect();
  }, [isExpanded, id]);

  return (
    <div id={id} className={cx(styles.item, className, isExpanded && styles.lastItem)}>
      <Heading>
        <button
          id={triggerId}
          type="button"
          className={cx(styles.trigger, isExpanded && styles.triggerExpanded)}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => toggle(id)}
        >
          <TitleSmallEmphasized className={styles.triggerLabel}>{title}</TitleSmallEmphasized>
          <ExpandMoreIcon
            className={cx(styles.chevron, isExpanded && styles.chevronExpanded)}
            aria-hidden="true"
          />
        </button>
      </Heading>

      <div
        ref={panelRef}
        id={panelId}
        {...(panelIsLandmark ? { role: 'region' as const, 'aria-labelledby': triggerId } : {})}
        hidden={!isExpanded}
        className={styles.panel}
      >
        <div className={styles.panelInner}>{children}</div>
      </div>
    </div>
  );
};
